import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDeals } from '../context/DataContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Wallet, Coins, TrendingUp, TrendingDown, Plus, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { fmtMoney } from '../lib/format';

// Deals whose commission is "earned" - same locked-in stages as elsewhere in the app
const LOCKED_STAGES = new Set(['rate_confirmed', 'contracted', 'complete']);

// ---- date helpers -----------------------------------------------------------
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const QUARTERS = [
  { id: 'Q1', label: 'Q1', months: [0, 1, 2],  name: 'Jan-Mar' },
  { id: 'Q2', label: 'Q2', months: [3, 4, 5],  name: 'Apr-Jun' },
  { id: 'Q3', label: 'Q3', months: [6, 7, 8],  name: 'Jul-Sep' },
  { id: 'Q4', label: 'Q4', months: [9,10,11],  name: 'Oct-Dec' },
];

const dealEarnDate = (d) => {
  // "month" is a manually-set field on the deal (deal.created_at, editable via the deals
  // table's month column) — commission counts toward whichever month the deal is marked as,
  // not whenever it happens to be invoiced/paid.
  if (!d.created_at) return null;
  const dt = new Date(d.created_at);
  return isNaN(dt.getTime()) ? null : dt;
};

const monthValueOfPackage = (p, monthISO) => {
  if (p.type === 'hourly') {
    const rate = Number(p.hourly_rate) || 0;
    const hrs = (p.hours_log || []).filter((h) => h.month === monthISO).reduce((a, h) => a + (Number(h.hours) || 0), 0);
    return hrs * rate;
  }
  // Compared by month (not exact day) — a retainer that started/ended mid-month still
  // counts for that whole month once its dates are edited.
  if (p.start_date && monthISO < String(p.start_date).slice(0, 7)) return 0;
  if (p.end_date && monthISO > String(p.end_date).slice(0, 7)) return 0;
  return Number(p.price) || 0;
};

// ============================================================================
const Finances = () => {
  const { user } = useAuth();
  const { deals, talent } = useDeals();
  const [clients, setClients] = React.useState([]);
  const [loadingClients, setLoadingClients] = React.useState(true);
  const [expenses, setExpenses] = React.useState([]);
  const [hours, setHours] = React.useState([]);
  const [directory, setDirectory] = React.useState([]);

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [period, setPeriod] = useState({ kind: 'year' });

  React.useEffect(() => {
    api.get('/clients').then(({ data }) => setClients(data || []))
      .catch(() => toast.error('failed to load clients'))
      .finally(() => setLoadingClients(false));
    api.get('/expenses').then(({ data }) => setExpenses(data || [])).catch(() => {});
    api.get('/hours').then(({ data }) => setHours(data || [])).catch(() => {});
    api.get('/team/directory').then(({ data }) => setDirectory(data || [])).catch(() => {});
  }, []);

  // years available from deals + client packages
  const years = useMemo(() => {
    // Always include current year, one before, and one ahead so managers can plan
    const set = new Set([currentYear - 1, currentYear, currentYear + 1]);
    (deals || []).forEach((d) => { const dt = dealEarnDate(d); if (dt) set.add(dt.getFullYear()); });
    (clients || []).forEach((c) => (c.packages || []).forEach((p) => {
      [p.start_date, p.end_date].forEach((iso) => { if (iso) set.add(new Date(iso).getFullYear()); });
      (p.hours_log || []).forEach((h) => { if (h.month) set.add(Number(h.month.slice(0, 4))); });
    }));
    return Array.from(set).sort((a, b) => b - a);
  }, [deals, clients, currentYear]);

  // Match helpers
  const dateInPeriod = (d) => {
    if (!d) return false;
    if (d.getFullYear() !== year) return false;
    if (period.kind === 'year') return true;
    if (period.kind === 'month') return d.getMonth() === period.m;
    if (period.kind === 'quarter') return QUARTERS.find((q) => q.id === period.q).months.includes(d.getMonth());
    return true;
  };
  const monthsInPeriod = () => {
    if (period.kind === 'year') return [...Array(12).keys()];
    if (period.kind === 'month') return [period.m];
    return QUARTERS.find((q) => q.id === period.q).months;
  };

  const talentIncome = useMemo(() => {
    let total = 0;
    let dealCount = 0;
    const byTalent = {};
    (deals || []).forEach((d) => {
      if (!LOCKED_STAGES.has(d.stage)) return;
      const dt = dealEarnDate(d);
      if (!dateInPeriod(dt)) return;
      const value = Number(d.value) || 0;
      // agency commission: prefer per-deal %, else fall back to talent's %
      const t = talent.find((x) => x.user_id === d.talent_id);
      const pct = (d.commission_percent != null ? d.commission_percent : (t?.commission_percent ?? 0)) || 0;
      const agencyCut = value * (pct / 100);
      total += agencyCut;
      dealCount += 1;
      if (t) byTalent[t.user_id] = { name: t.name, amount: (byTalent[t.user_id]?.amount || 0) + agencyCut };
    });
    return { total, dealCount, breakdown: Object.values(byTalent).sort((a, b) => b.amount - a.amount) };
  }, [deals, talent, year, period]);

  const clientIncome = useMemo(() => {
    let total = 0;
    const months = monthsInPeriod();
    const byClient = {};
    (clients || []).forEach((c) => {
      let sub = 0;
      (c.packages || []).forEach((p) => {
        months.forEach((mi) => {
          const monthISO = `${year}-${String(mi + 1).padStart(2, '0')}`;
          sub += monthValueOfPackage(p, monthISO);
        });
      });
      (c.ad_hoc_projects || []).forEach((p) => {
        const iso = p.invoiced_at || p.completed_at || p.created_at;
        if (!iso) return;
        const dt = new Date(iso);
        if (!dateInPeriod(dt)) return;
        sub += Number(p.fee) || 0;
      });
      if (sub > 0) byClient[c.id] = { name: c.name, logo: c.logo, amount: sub };
      total += sub;
    });
    return { total, breakdown: Object.values(byClient).sort((a, b) => b.amount - a.amount) };
  }, [clients, year, period]);

  // Expenses ------------------------------------------------------------------
  // Ad-hoc: hits the month of `date`.
  // Recurring: applies once per month from `date`'s month until `end_date` (or forever).
  const expensesForKind = (kind) => {
    let total = 0;
    const rows = [];
    const months = monthsInPeriod();
    (expenses || []).forEach((e) => {
      if (e.kind !== kind) return;
      const amount = Number(e.amount) || 0;
      if (!amount) return;
      const related = kind === 'talent'
        ? talent.find((t) => t.user_id === e.related_id)
        : clients.find((c) => c.id === e.related_id);
      const baseName = e.label || related?.name || (kind === 'talent' ? 'talent expense' : 'client expense');
      const name = e.emoji ? `${e.emoji} ${baseName}` : baseName;
      if (e.recurring) {
        const start = e.date ? new Date(e.date) : null;
        const end = e.end_date ? new Date(e.end_date) : null;
        months.forEach((mi) => {
          const monthDate = new Date(year, mi, 1);
          const monthEnd = new Date(year, mi + 1, 0);
          if (start && monthEnd < start) return;
          if (end && monthDate > end) return;
          total += amount;
          rows.push({
            ...e,
            id: `${e.id}_${year}-${mi}`,
            _name: `${name} · ${MONTHS[mi]}`,
            _logo: related?.logo,
            amount,
            date: `${year}-${String(mi + 1).padStart(2, '0')}-01`,
            _recurring: true,
            _origId: e.id,
          });
        });
      } else {
        const dt = e.date ? new Date(e.date) : null;
        if (!dateInPeriod(dt)) return;
        total += amount;
        rows.push({ ...e, _name: name, _logo: related?.logo });
      }
    });
    return { total, rows: rows.sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))) };
  };

  const talentExpenses = useMemo(() => expensesForKind('talent'), [expenses, talent, year, period]);
  const clientExpenses = useMemo(() => expensesForKind('client'), [expenses, clients, year, period]);

  // Fold logged hours into the expense breakdown. Client-tagged hours = client expense.
  // LVE-Social-tagged hours (`__lve__`) = talent expense.
  const hoursExpenses = useMemo(() => {
    const rateMap = Object.fromEntries((directory || []).map((u) => [u.user_id, Number(u.hourly_rate) || 0]));
    const nameMap = Object.fromEntries((directory || []).map((u) => [u.user_id, u.name]));
    let talentTotal = 0, clientTotal = 0;
    const talentRows = [], clientRows = [];
    (hours || []).forEach((h) => {
      const rate = rateMap[h.user_id];
      if (!rate) return;
      const dt = h.date ? new Date(h.date) : null;
      if (!dateInPeriod(dt)) return;
      const cost = (Number(h.hours) || 0) * rate;
      if (!cost) return;
      const uName = (nameMap[h.user_id] || 'someone').split(' ')[0].toUpperCase();
      if (h.client_id === '__lve__') {
        talentTotal += cost;
        talentRows.push({ id: 'hr_' + h.id, _name: `${uName} HOURS`, amount: cost, date: h.date, _hours: true });
      } else if (h.client_id) {
        clientTotal += cost;
        const cli = clients.find((c) => c.id === h.client_id);
        clientRows.push({ id: 'hr_' + h.id, _logo: cli?.logo, _name: `${uName} HOURS`, amount: cost, date: h.date, _hours: true });
      }
    });
    return { talentTotal, clientTotal, talentRows, clientRows };
  }, [hours, directory, clients, year, period]);

  const talentExpensesFinal = {
    total: talentExpenses.total + hoursExpenses.talentTotal,
    rows: [...hoursExpenses.talentRows, ...talentExpenses.rows],
  };
  const clientExpensesFinal = {
    total: clientExpenses.total + hoursExpenses.clientTotal,
    rows: [...hoursExpenses.clientRows, ...clientExpenses.rows],
  };
  const totalExpenses = talentExpensesFinal.total + clientExpensesFinal.total;

  const combined = talentIncome.total + clientIncome.total;
  const pnl = combined - totalExpenses;

  const addExpense = async (kind) => {
    const label = window.prompt(`Describe this ${kind} expense`);
    if (!label) return;
    const amountRaw = window.prompt('Amount (£)');
    if (amountRaw == null) return;
    const amount = Number(amountRaw);
    if (isNaN(amount)) { toast.error('invalid amount'); return; }
    const today = new Date().toISOString().slice(0, 10);
    try {
      const { data } = await api.post('/expenses', { kind, label, amount, date: today });
      setExpenses((prev) => [data, ...prev]);
    } catch { toast.error('failed to add expense'); }
  };

  const removeExpense = async (id) => {
    if (!window.confirm('remove this expense?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      setExpenses((prev) => prev.filter((e) => e.id !== id));
    } catch { toast.error('failed to delete'); }
  };

  const periodLabel = period.kind === 'year' ? String(year)
    : period.kind === 'month' ? `${MONTHS[period.m]} ${year}`
      : `${period.q} ${year} · ${QUARTERS.find((q) => q.id === period.q).name}`;

  return (
    <div className="p-6 lg:p-8">
      <header className="flex items-end justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <Wallet className="w-6 h-6 text-pink-500" /> finances
          </h1>
          <p className="text-sm text-slate-500 mt-1">agency commission + retainer revenue by month, quarter or year.</p>
        </div>
        <div className="inline-flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-slate-500">year</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </header>

      {/* Period filter row */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6" data-testid="finance-period-filter">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setPeriod({ kind: 'year' })}
            className={`h-8 px-3 rounded-full text-[11px] font-semibold transition ${period.kind === 'year' ? 'bg-pink-500 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            data-testid="finance-period-year"
          >full year</button>
          {QUARTERS.map((q) => (
            <button
              key={q.id}
              onClick={() => setPeriod({ kind: 'quarter', q: q.id })}
              className={`h-8 px-3 rounded-full text-[11px] font-semibold transition ${period.kind === 'quarter' && period.q === q.id ? 'bg-pink-500 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              title={q.name}
              data-testid={`finance-period-${q.id.toLowerCase()}`}
            >{q.label}</button>
          ))}
          <select
            value={period.kind === 'month' ? String(period.m) : ''}
            onChange={(e) => e.target.value === '' ? setPeriod({ kind: 'year' }) : setPeriod({ kind: 'month', m: Number(e.target.value) })}
            className={`h-8 px-3 rounded-full text-[11px] font-semibold transition border-0 outline-none focus:ring-2 focus:ring-pink-200 ${period.kind === 'month' ? 'bg-pink-500 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            data-testid="finance-period-month-select"
          >
            <option value="">by month…</option>
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
        </div>
      </div>

      {/* Summary cards — side by side */}
      <div className="grid grid-cols-2 gap-4">
        <SummaryCard
          title="Talent Income"
          subtitle={`agency commission · ${periodLabel}`}
          icon={<TrendingUp className="w-5 h-5 text-emerald-500" />}
          total={talentIncome.total}
          breakdown={talentIncome.breakdown}
          emptyLabel="no commissioned deals in this window"
          testId="finance-talent-income"
        />
        <SummaryCard
          title="Client Income"
          subtitle={`retainer + ad-hoc · ${periodLabel}`}
          icon={<TrendingUp className="w-5 h-5 text-emerald-500" />}
          total={clientIncome.total}
          breakdown={clientIncome.breakdown}
          emptyLabel="no client revenue in this window"
          testId="finance-client-income"
        />
      </div>

      <div className="mt-4 bg-slate-900 text-white rounded-2xl p-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">total income · {periodLabel}</div>
          <div className="text-2xl font-semibold mt-1">{fmtMoney(combined)}</div>
        </div>
        <div className="text-[11px] text-slate-400 text-right">
          <div>{talentIncome.dealCount} commissioned deals</div>
          <div>{clientIncome.breakdown.length} clients billed</div>
        </div>
      </div>

      {/* Expense boxes */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        <ExpenseCard
          title="Talent Expenses"
          subtitle={periodLabel}
          icon={<span className="text-lg leading-none">💳</span>}
          total={talentExpensesFinal.total}
          rows={talentExpensesFinal.rows}
          onAdd={() => addExpense('talent')}
          onRemove={removeExpense}
          testId="finance-talent-expenses"
        />
        <ExpenseCard
          title="Client Expenses"
          subtitle={periodLabel}
          icon={<span className="text-lg leading-none">💳</span>}
          total={clientExpensesFinal.total}
          rows={clientExpensesFinal.rows}
          onAdd={() => addExpense('client')}
          onRemove={removeExpense}
          testId="finance-client-expenses"
        />
      </div>

      <div className="mt-4 bg-slate-800 text-white rounded-2xl p-4 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-400">total expenses · {periodLabel}</div>
        <div className="text-2xl font-semibold tabular-nums">{fmtMoney(totalExpenses)}</div>
      </div>

      <div className={`mt-4 rounded-2xl p-5 flex items-center justify-between ${pnl >= 0 ? 'bg-pink-500' : 'bg-rose-600'} text-white shadow-md`}>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-pink-100">P & L · {periodLabel}</div>
          <div className="text-[11px] text-pink-100 mt-0.5">income − expenses</div>
        </div>
        <div className="text-3xl font-semibold tabular-nums">{fmtMoney(pnl)}</div>
      </div>
    </div>
  );
};

const SummaryCard = ({ title, subtitle, icon, total, breakdown, emptyLabel, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{title}</div>
    </div>
    <div className="text-2xl font-semibold text-slate-900 tabular-nums">{fmtMoney(total)}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
    <div className="mt-3 border-t border-slate-100 pt-3">
      {breakdown.length === 0 ? (
        <div className="text-xs text-slate-400 italic">{emptyLabel}</div>
      ) : (
        <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
          {breakdown.slice(0, 15).map((row, i) => (
            <li key={i} className="flex items-center justify-between text-[13px]">
              <span className="text-slate-700 truncate flex items-center gap-1.5 min-w-0">{row.logo && <span className="shrink-0">{row.logo}</span>}<span className="truncate">{row.name}</span></span>
              <span className="text-slate-900 font-medium tabular-nums shrink-0 ml-2">{fmtMoney(row.amount)}</span>
            </li>
          ))}
          {breakdown.length > 15 && <li className="text-[11px] text-slate-400 italic">+ {breakdown.length - 15} more</li>}
        </ul>
      )}
    </div>
  </div>
);

const ExpenseCard = ({ title, subtitle, icon, total, rows, onAdd, onRemove, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{title}</div>
      <button
        onClick={onAdd}
        className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-pink-600 hover:text-pink-700"
        data-testid={`${testId}-add`}
      ><Plus className="w-3 h-3" /> add</button>
    </div>
    <div className="text-2xl font-semibold text-slate-900 tabular-nums">{fmtMoney(total)}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
    <div className="mt-3 border-t border-slate-100 pt-3">
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic">no expenses logged in this window</div>
      ) : (
        <ul className="space-y-1.5 h-60 overflow-y-auto pr-1">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between text-[13px] group">
              <span className="text-slate-700 truncate flex items-center gap-1.5 min-w-0">
                {r._logo && <span className="shrink-0">{r._logo}</span>}
                <span className="truncate">{r._name}</span>
                {r.date && <span className="text-[10px] text-slate-400 shrink-0">· {r.date.slice(5)}</span>}
              </span>
              <span className="flex items-center gap-2 shrink-0 ml-2">
                <span className="text-slate-900 font-medium tabular-nums">{fmtMoney(r.amount)}</span>
                {!r._recurring && !r._hours && (
                  <button onClick={() => onRemove(r.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600" title="remove"><Trash2 className="w-3 h-3" /></button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

export default Finances;
